import React, { useEffect, useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { requestRegistrationCardPublicAPI } from '../../api/charge';
import { colors } from '../../lib/colors';
import { Body2Regular, Body3Regular, Head4, CaptionRegular } from '../../lib/font';
import { Col, Row } from '../../lib/utils';
import Loading from '../../components/Loading';

const InputPaymentSuccessPage = () => {
    const { adminId } = useParams<{ adminId: string }>();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const authKey = queryParams.get('authKey') || '';
    const customerKey = queryParams.get('customerKey') || '';
    const managerName = queryParams.get('managerName') || '';
    const centerName = queryParams.get('centerName') || '';

    const [isLoading, setIsLoading] = useState(true);
    const [isSuccess, setIsSuccess] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [cardInfo, setCardInfo] = useState<{ cardIssuingBank: string; cardNumber: string } | null>(null);

    useEffect(() => {
        const registerCard = async () => {
            if (!adminId || !authKey || !customerKey) {
                setErrorMessage('필수 정보가 누락되었습니다.');
                setIsLoading(false);
                return;
            }

            try {
                const extra = managerName || centerName
                    ? { managerName: managerName || undefined, centerName: centerName || undefined }
                    : undefined;
                const res = await requestRegistrationCardPublicAPI(adminId, authKey, customerKey, extra);
                if (res.status === 201 && res.data?.success) {
                    setIsSuccess(true);
                    setCardInfo({
                        cardIssuingBank: res.data.cardIssuingBank,
                        cardNumber: res.data.cardNumber,
                    });
                } else {
                    setErrorMessage(res.data?.message || '카드 등록에 실패했습니다.');
                }
            } catch {
                setErrorMessage('서버에 연결할 수 없습니다.');
            }
            setIsLoading(false);
        };

        registerCard();
    }, [adminId, authKey, customerKey]);

    if (isLoading) {
        return <Loading text="카드 정보 등록 진행중입니다. 잠시만 기다려주세요." />;
    }

    return (
        <Col style={{ width: '100%', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', padding: 16 }}>
            <Col
                style={{
                    width: '100%',
                    maxWidth: 480,
                    borderRadius: 16,
                    backgroundColor: colors.WHITE_50,
                    border: isSuccess ? '1px solid #C3D4F4' : '1px solid #FFCDD2',
                    padding: '32px 24px',
                    alignItems: 'center',
                }}
            >
                {isSuccess ? (
                    <>
                        <div
                            style={{
                                width: 64,
                                height: 64,
                                borderRadius: 32,
                                backgroundColor: '#E8F5E9',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: 28,
                            }}
                        >
                            &#10003;
                        </div>
                        <Head4 style={{ color: colors.LAVEL_4, marginTop: 20 }}>카드 등록 완료</Head4>
                        <Body2Regular style={{ color: colors.LAVEL_3, marginTop: 8, textAlign: 'center' }}>
                            카드가 정상적으로 등록되었습니다.
                        </Body2Regular>

                        {cardInfo && (
                            <Col
                                style={{
                                    width: '100%',
                                    marginTop: 24,
                                    padding: 20,
                                    borderRadius: 12,
                                    backgroundColor: colors.WHITE_300,
                                    border: '1px solid #E0E0E0',
                                }}
                            >
                                <Row style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                                    <CaptionRegular style={{ color: colors.LAVEL_2 }}>카드사</CaptionRegular>
                                    <Body3Regular style={{ color: colors.LAVEL_4 }}>
                                        {cardInfo.cardIssuingBank}
                                    </Body3Regular>
                                </Row>
                                <Row
                                    style={{
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        marginTop: 12,
                                    }}
                                >
                                    <CaptionRegular style={{ color: colors.LAVEL_2 }}>카드번호</CaptionRegular>
                                    <Body3Regular style={{ color: colors.LAVEL_4 }}>
                                        {cardInfo.cardNumber}
                                    </Body3Regular>
                                </Row>
                            </Col>
                        )}

                        <CaptionRegular style={{ color: colors.LAVEL_1, marginTop: 24, textAlign: 'center' }}>
                            이 페이지를 닫으셔도 됩니다.
                        </CaptionRegular>
                    </>
                ) : (
                    <>
                        <div
                            style={{
                                width: 64,
                                height: 64,
                                borderRadius: 32,
                                backgroundColor: '#FFEBEE',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: 28,
                                color: colors.ERROR,
                            }}
                        >
                            !
                        </div>
                        <Head4 style={{ color: colors.ERROR, marginTop: 20 }}>카드 등록 실패</Head4>
                        <Body2Regular style={{ color: colors.LAVEL_3, marginTop: 8, textAlign: 'center' }}>
                            {errorMessage}
                        </Body2Regular>
                        <button
                            onClick={() => window.history.back()}
                            style={{
                                marginTop: 24,
                                padding: '10px 24px',
                                borderRadius: 8,
                                backgroundColor: colors.primary,
                                color: 'white',
                                border: 'none',
                                cursor: 'pointer',
                                fontSize: 14,
                            }}
                        >
                            다시 시도하기
                        </button>
                    </>
                )}
            </Col>
        </Col>
    );
};

export default InputPaymentSuccessPage;
